import { useState, useEffect } from 'react';
import { settingsAPI } from '@/lib/api';

/**
 * Hook for managing listing grid settings across all pages
 * Returns gridSettings, isMobile, singleColumn flag, and getGridStyle function
 */
export const useListingGrid = (initialSettings = null) => {
    const [gridSettings, setGridSettings] = useState(initialSettings || {
        desktop: 4,
        tablet: 3,
        mobile: 2
    });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);
    const [settingsLoaded, setSettingsLoaded] = useState(!!initialSettings);

    // Detect screen size
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
            setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Load settings from API if not provided
    useEffect(() => {
        if (initialSettings) return;
        
        const loadSettings = async () => {
            try {
                const res = await settingsAPI.get();
                if (res.data) {
                    setGridSettings({
                        desktop: res.data.grid_columns_desktop || 4,
                        tablet: res.data.grid_columns_tablet || 3,
                        mobile: res.data.grid_columns_mobile || 2
                    });
                }
            } catch (error) {
                console.error('Error loading grid settings:', error);
            } finally {
                setSettingsLoaded(true);
            }
        };
        loadSettings();
    }, [initialSettings]);

    // Check if single column mode is active
    const singleColumn = isMobile && gridSettings.mobile === 1;

    // Generate CSS grid style
    const getGridStyle = () => ({
        '--grid-cols-mobile': gridSettings.mobile,
        '--grid-cols-tablet': gridSettings.tablet,
        '--grid-cols-desktop': gridSettings.desktop,
        display: 'grid',
        gap: '1.5rem',
        gridTemplateColumns: `repeat(var(--grid-cols-mobile), minmax(0, 1fr))`,
    });

    // CSS class for responsive grid (to be used with Tailwind)
    const gridClassName = `grid gap-6 grid-cols-${gridSettings.mobile} md:grid-cols-${gridSettings.tablet} lg:grid-cols-${gridSettings.desktop}`;

    return {
        gridSettings,
        setGridSettings,
        isMobile,
        isTablet,
        singleColumn,
        settingsLoaded,
        getGridStyle,
        gridClassName
    };
};

export default useListingGrid;
